import { useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Briefcase, Heart, LogOut, ShoppingBag, User, type LucideIcon } from "lucide-react";
import { signOut, useSession } from "../../lib/auth";
import { AccountModal, type TabId as AccountTabId } from "../../features/account/components/account-modal";
import { floatPanelStyle, popOrigin, useMenu, usePresence, type MenuGroup } from "./use-presence";
import { useBucket } from "../../features/bucket/state/bucket-context";

const NAVY = "#110E39";
const MUTED = "#5a5d77";
const RED = "#9B2C2C";
const RED_TINT = "rgba(190,60,60,0.13)";
const HAIRLINE = "rgba(17,14,57,0.09)";
const SOFT_TINT = "rgba(17,14,57,0.055)";
const GOLD = "#E5B265";
const NAVY_GRADIENT = "linear-gradient(135deg, #2E2878 0%, #1A1454 48%, #110E39 100%)";
const PRIMARY_SHADOW = "0 10px 24px -12px rgba(17,14,57,0.8), inset 0 1px 0 rgba(255,255,255,0.16)";

type ItemProps = {
  readonly icon: LucideIcon;
  readonly label: string;
  readonly href?: string;
  readonly onClick?: () => void;
  readonly danger?: boolean;
  /** A count on the end of the row (the bucket's items). */
  readonly count?: number;
};

function MenuItem({ icon: ItemIcon, label, href, onClick, danger, count }: ItemProps) {
  const navigate = useNavigate();
  const className = "flex w-full items-center gap-3 rounded-[12px] px-3 py-2.5 text-start hover:bg-black/5";
  const inner = (
    <>
      <span
        aria-hidden="true"
        className="grid h-9 w-9 shrink-0 place-items-center rounded-full"
        style={{ background: danger ? RED_TINT : SOFT_TINT }}
      >
        <ItemIcon size={17} strokeWidth={2.2} style={{ color: danger ? RED : NAVY }} />
      </span>
      <span className="min-w-0 flex-1 break-words text-[13.5px] font-bold" style={{ color: danger ? RED : NAVY }}>
        {label}
      </span>
      {count ? (
        <span
          className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1.5 text-[11px] font-bold tabular-nums"
          style={{ background: GOLD, color: NAVY }}
        >
          {count > 99 ? "99+" : count}
        </span>
      ) : null}
    </>
  );

  function handle(e: React.MouseEvent) {
    onClick?.();
    if (href) {
      e.preventDefault();
      navigate({ href });
    }
  }

  if (href) {
    return (
      <a href={href} role="menuitem" onClick={handle} className={className}>
        {inner}
      </a>
    );
  }
  return (
    <button type="button" role="menuitem" onClick={onClick} className={className}>
      {inner}
    </button>
  );
}

type Face = { image?: unknown; initials: string };

/** The guest's picture, their initials, or the house placeholder, in that order. */
function AvatarFace({ image, initials, size, fontSize }: Face & { size: number; fontSize: number }) {
  return (
    <span
      className="flex shrink-0 items-center justify-center overflow-hidden rounded-full"
      style={{ width: size, height: size, background: NAVY_GRADIENT }}
    >
      {image ? (
        <img src={image as string} alt="" className="h-full w-full object-cover" />
      ) : initials ? (
        <span className="font-bold text-white" style={{ fontSize }}>{initials}</span>
      ) : (
        <img src="/images/default-avatar.png" alt="" className="h-full w-full object-cover" />
      )}
    </span>
  );
}

export function AvatarDropdown({ className, group }: { readonly className?: string; readonly group?: MenuGroup }) {
  const { data: session } = useSession();
  const menu = useMenu(group, "avatar");
  const presence = usePresence(menu.open);
  const [accountTab, setAccountTab] = useState<AccountTabId | null>(null);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { lang } = (useParams({ strict: false }) as unknown) as { lang?: string };
  const locale = lang ?? "en";
  const { count: bucketCount } = useBucket();

  if (!session?.user) return null;

  const user = session.user;
  const displayName = (user.name as string | undefined) ?? user.email ?? t("nav.account");
  const initials = displayName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

  function openAccount(tab: AccountTabId) {
    menu.close();
    setAccountTab(tab);
  }

  async function handleSignOut() {
    menu.close();
    await signOut();
    navigate({ href: "/sign-in" });
  }

  return (
    <div ref={menu.ref} className={["relative shrink-0", className].filter(Boolean).join(" ")}>
      <button
        type="button"
        onClick={menu.toggle}
        aria-haspopup="menu"
        aria-expanded={menu.open}
        aria-label={bucketCount > 0 ? `${displayName}, ${t("nav.bucket_count", { count: bucketCount })}` : displayName}
        className="mj-press flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full"
        style={{ boxShadow: menu.open ? `${PRIMARY_SHADOW}, 0 0 0 2px rgba(17,14,57,0.4)` : PRIMARY_SHADOW }}
      >
        <AvatarFace image={user.image} initials={initials} size={40} fontSize={13} />
      </button>
      {/* The bucket lives in this menu now (owner, 2026-10-11), so its count
          rides on the avatar. Decorative: the button's label says it. */}
      {bucketCount > 0 ? (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -end-0.5 -top-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold leading-none tabular-nums"
          style={{ background: GOLD, color: NAVY, boxShadow: "0 0 0 2px #fff" }}
        >
          {bucketCount > 99 ? "99+" : bucketCount}
        </span>
      ) : null}

      {presence.mounted ? (
        <div
          ref={presence.ref}
          role="menu"
          aria-label={t("nav.account_menu", { defaultValue: "Account menu" })}
          data-state={presence.state}
          data-instant={menu.instant ? "true" : undefined}
          className={`mj-pop absolute end-0 top-[calc(100%+8px)] z-[var(--z-dropdown)] w-[280px] p-1.5 ${
            presence.state === "closed" ? "pointer-events-none" : ""
          }`}
          style={{ ...floatPanelStyle(18), transformOrigin: popOrigin("end", i18n.dir()) }}
        >
          <div className="mb-1 flex items-center gap-3 rounded-[12px] p-3" style={{ background: SOFT_TINT }}>
            <AvatarFace image={user.image} initials={initials} size={44} fontSize={14} />
            <span className="min-w-0 flex-1">
              <span className="line-clamp-2 break-words text-[13.5px] font-bold" style={{ color: NAVY }}>{displayName}</span>
              {/* clamp-ok: guest's own email */}
              {user.email && (
                <span className="block truncate text-[11.5px]" style={{ color: MUTED }}>{user.email}</span>
              )}
            </span>
          </div>

          <MenuItem
            icon={User}
            label={t("nav.my_account", { defaultValue: "My account" })}
            onClick={() => openAccount("overview")}
          />
          <MenuItem
            icon={Briefcase}
            label={t("nav.reservations", { defaultValue: "Reservations" })}
            onClick={() => openAccount("bookings")}
          />
          <MenuItem
            icon={Heart}
            label={t("nav.saved", { defaultValue: "Saved" })}
            onClick={() => openAccount("saved")}
          />
          <MenuItem icon={ShoppingBag} label={t("nav.bucket", { defaultValue: "Bucket" })} href={`/${locale}/bucket`} onClick={menu.close} count={bucketCount} />

          <div className="my-1.5" style={{ borderTop: `1px solid ${HAIRLINE}` }} />

          <MenuItem
            icon={LogOut}
            label={t("common.sign_out", { defaultValue: "Sign out" })}
            onClick={handleSignOut}
            danger
          />
        </div>
      ) : null}

      {accountTab ? (
        <AccountModal initialTab={accountTab} onClose={() => setAccountTab(null)} />
      ) : null}
    </div>
  );
}
